import {
    createUnifiedTheme,
    createBaseThemeOptions,
    pageTheme as defaultPageThemes,
    PageTheme,
    palettes,
    UnifiedTheme,
} from '@backstage/theme';
import { AutocompleteClassKey } from '@material-ui/lab/Autocomplete';
import { AlertClassKey } from '@material-ui/lab/Alert';
import { alpha } from '@material-ui/core/styles';

// Labs types not included in overrides
declare module '@material-ui/core/styles/overrides' {
    export interface ComponentNameToClassKey {
        MuiAlert: AlertClassKey;
        MuiAutocomplete: AutocompleteClassKey;
    }
}

const pageThemesFontColorOverride: Record<string, PageTheme> = {};
Object.keys(defaultPageThemes).forEach(key => {
    pageThemesFontColorOverride[key] = {
        ...defaultPageThemes[key],
        fontColor: '#FAFAFA',
    };
});

export const modernTheme: UnifiedTheme = createUnifiedTheme({
    fontFamily: '"Plus Jakarta Sans", "Inter", system-ui, sans-serif',
    pageTheme: pageThemesFontColorOverride,
    defaultPageTheme: 'home',
    ...createBaseThemeOptions({
        palette: {
            ...palettes.dark,
            primary: {
                main: '#90caf9',
                light: '#e3f2fd',
                dark: '#42a5f5',
            },
            secondary: {
                main: '#CE93D8',
                light: '#E1BEE7',
                dark: '#AB47BC',
                contrastText: '#000000'
            },
            grey: {
                50: '#FAFAFA',
                100: '#F4F4F5',
                200: '#E4E4E7',
                300: '#D4D4D8',
                400: '#A1A1AA',
                500: '#71717A',
                600: '#52525B',
                700: '#3F3F46',
                800: '#27272A',
                900: '#18181B',
            },
            error: {
                main: '#F43F5E',
                light: '#FB7185',
                dark: '#E11D48',
            },
            warning: {
                main: '#F59E0B',
                light: '#FBBF24',
                dark: '#D97706',
            },
            success: {
                main: '#10B981',
                light: '#34D399',
                dark: '#059669',
            },
            info: {
                main: '#0EA5E9',
                light: '#38BDF8',
                dark: '#0284C7',
            },
            navigation: {
                ...palettes.dark.navigation,
                background: '#18181B',
                color: '#FAFAFA',
                indicator: '#90caf9',
                submenu: {
                    background: '#27272A'
                },
                navItem: {
                    hoverBackground: alpha('#90caf9', 0.1),
                },
            },
            text: {
                primary: '#FAFAFA',
            },
            background: {
                default: '#18181B',
            },
        }
    }),
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                '@global': {
                    div: {
                        '&[class^="BackstageSidebar-drawer-"]': {
                            borderRight: '1px solid rgba(255, 255, 255, 0.08)'
                        },
                        '&[class*="makeStyles-entityTypeBox-"]': {
                            background: 'none',
                            marginLeft: '4px',
                            backgroundColor: '#18181B'
                        }
                    },
                    a: {
                        '&[class^="BackstageSidebarSubmenuItem-selected-"]': {
                            backgroundColor: '#CE93D8'
                        },
                    },
                    hr: {
                        '&[class^="hr-root-"]': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)'
                        }
                    }
                }
            }
        },
        BackstageContent: {
            styleOverrides: {
                root: {
                    backgroundColor: '#18181B'
                },
            },
        },
        BackstagePage: {
            styleOverrides: {
                root: {
                    borderLeft: '1px solid rgba(255, 255, 255, 0.08)'
                },
            },
        },
        BackstageHeader: {
            styleOverrides: {
                header: {
                    borderBottom: '0px solid rgba(255, 255, 255, 0.1)',
                    backgroundImage: 'unset',
                    boxShadow: '0 0px 0px 0 rgb(0 0 0 / 0.1)',
                    backgroundColor: '#27272A',
                    paddingBottom: '24px',
                },
                title: {
                    color: '#FAFAFA',
                    fontSize: '1.75rem',
                    fontWeight: 700,
                    letterSpacing: '-0.025em',
                },
                subtitle: {
                    color: alpha('#E4E4E7', 0.7),
                },
                breadcrumb: {
                    color: alpha('#FAFAFA', 0.7),
                },
                breadcrumbTitle: {
                    color: alpha('#FAFAFA', 0.7),
                },
                type: {
                    color: alpha('#FAFAFA', 0.7),
                },
            },
        },
        BackstageHeaderLabel: {
            styleOverrides: {
                label: {
                    color: '#FAFAFA',
                },
                value: {
                    color: alpha('#FAFAFA', 0.7),
                },
            },
        },
        BackstageHeaderTabs: {
            styleOverrides: {
                tabRoot: {
                    backgroundColor: '#27272A'
                },
                tabsWrapper: {
                    backgroundColor: '#27272A'
                },
                defaultTab: {
                    color: alpha('#FAFAFA', 0.7)
                },
                selected: {
                    color: '#FFFFFF'
                }
            },
        },
        BackstageSidebar: {
            styleOverrides: {
                drawer: {
                    backgroundColor: '#18181B',
                    borderRightColor: 'rgba(255, 255, 255, 0.08)',
                    "& hr": {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        width: '100%',
                        height: '1px',
                        border: 'none'
                    }
                }
            }
        },
        BackstageSidebarDivider: {
            styleOverrides: {
                root: {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    width: '100%',
                    height: '1px',
                    border: 'none'
                }
            },
        },
        BackstageSidebarItem: {
            styleOverrides: {
                root: {
                    textDecoration: 'none',
                    "& h6": {
                        color: '#FAFAFA'
                    },
                },
                closedItemIcon: {
                    color: '#FAFAFA'
                },
                highlighted: {
                    color: '#CE93D8'
                }
            },
        },
        BackstageItemCardHeader: {
            styleOverrides: {
                root: {
                    backgroundColor: '#27272A',
                    backgroundImage: 'unset',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                }
            },
        },
        BackstageTable: {
            styleOverrides: {
                root: {
                    '&> :first-child': {
                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                        boxShadow: 'none',
                    },
                },
            },
        },
        BackstageOpenedDropdown: {
            styleOverrides: {
                icon: {
                    '& path': {
                        fill: '#FAFAFA',
                    },
                },
            },
        },
        BackstageClosedDropdown: {
            styleOverrides: {
                icon: {
                    '& path': {
                        fill: '#FAFAFA',
                    },
                },
            },
        },
        BackstageEmptyState: {
            styleOverrides: {
                action: {
                    '& span[class^="MuiButton-label"]': {
                        color: '#FAFAFA'
                    }
                }
            }
        },
        BackstageAvatar: {
            styleOverrides: {
                avatar: {
                    marginRight: '15px',
                    fontFamily: '"Plus Jakarta Sans", "Inter", system-ui, sans-serif',
                    backgroundColor: '#3F3F46'
                }
            }
        },
        BackstageSelect: {
            styleOverrides: {
                root: {
                    backgroundColor: '#27272A'
                }
            }
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    "& h3": {
                        color: '#90caf9'
                    }
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    textTransform: 'none',
                    fontWeight: 500
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    textTransform: 'none',
                    backgroundColor: '#27272A'
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    textTransform: 'none'
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    textTransform: 'none'
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    textTransform: 'none'
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    padding: 10,
                },
                switchBase: {
                    padding: 12,
                },
                thumb: {
                    backgroundColor: '#FAFAFA',
                    height: 14,
                    width: 14,
                },
                track: {
                    borderRadius: 9,
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: '#CE93D8'
                },
            },
        },
        MuiGrid: {
            styleOverrides: {
                root: {
                    borderColor: '1px solid rgba(255, 255, 255, 0.08)'
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                    backgroundColor: '#27272A',
                    marginLeft: '10px'
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    color: '#FAFAFA',
                    backgroundColor: '#3F3F46',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: 'rgba(255, 255, 255, 0.08)',
                    marginLeft: '2px'
                },
                outlined: {
                    backgroundColor: '#3F3F46',
                    borderColor: 'rgba(255, 255, 255, 0.08)'
                }
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:nth-of-type(odd)': {
                        backgroundColor: '#27272A',
                    },
                    '&:nth-of-type(even)': {
                        backgroundColor: '#18181B',
                    },
                }
            }
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    '&:nth-of-type(odd)': {
                        backgroundColor: '#27272A',
                    },
                    '&:nth-of-type(even)': {
                        backgroundColor: '#18181B',
                    }
                },
                selected: {
                    "& svg": {
                        color: "#CE93D8"
                    }
                }
            }
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: '#90caf9',
                    "& h6": {
                        color: '#90caf9'
                    }
                },
                underlineHover: {
                    color: '#90caf9',
                    textDecoration: 'none',
                    outline: 0,
                    "& svg": {
                        color: '#90caf9'
                    }
                }
            }
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginTop: '15px',
                    padding: '5px'
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    padding: '5px'
                }
            }
        },
        CatalogReactUserListPicker: {
            styleOverrides: {
                root: {
                    background: alpha('#90caf9', 0.1)
                }
            }
        },
        MuiBottomNavigationAction: {
            styleOverrides: {
                root: {
                    "& svg": {
                        color: '#90caf9'
                    }
                }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                root: {

                }
            }
        }
    }
});
